import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useMemo, useState } from "react";

import {
  type ManagedMcpServer,
  useManagedMcpServers,
} from "./useManagedMcpServers.ts";
import type { McpAccessLogEvent } from "./useMcpAccessLogs.ts";
import type { McpClient } from "./useMcpClients.ts";

type EventsResp = { events: McpAccessLogEvent[] };

const DEFAULT_LIMIT = 200;

const eventServerId = (e: McpAccessLogEvent): string | undefined =>
  e.event?.["mcp-server"]?.id;

/**
 * Resolves the set of managed MCP servers a given registered client has been
 * observed contacting, by issuing a server-side `where(decodedToken.azp ==
 * client.id)` query against the access-log collection and joining the
 * distinct `event.mcp-server.id` values against the tenant's MCP integration
 * registry. Only servers present in both the registry and the observed
 * traffic are returned, so callers can render edges client → gateway →
 * server without surfacing stale or unmanaged ids.
 */
export const useEnabledServersForClient = (
  client: Pick<McpClient, "id"> | undefined,
  options: { limit?: number } = {}
): {
  servers: ManagedMcpServer[];
  observedServerIds: ReadonlySet<string>;
  loading: boolean;
} => {
  const authFetch = useAuthFetch();
  const { servers: managed, loading: managedLoading } = useManagedMcpServers();
  const [observed, setObserved] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const limit = options.limit ?? DEFAULT_LIMIT;
  const clientId = client?.id;

  useGuardedEffect(
    (cancellation) => async () => {
      if (!clientId) {
        setObserved(new Set());
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          clientId,
          limit: String(limit),
        });
        const res = await authFetch(
          `mcp-access-log/events?${params.toString()}`,
          { method: "GET", cancellation }
        );
        if (!res || cancellation.isCancelled) return;
        const body = (await res.json()) as EventsResp;
        if (cancellation.isCancelled) return;
        const ids = new Set<string>();
        for (const event of body.events ?? []) {
          const id = eventServerId(event);
          if (id) ids.add(id);
        }
        setObserved(ids);
      } finally {
        if (!cancellation.isCancelled) setLoading(false);
      }
    },
    [authFetch, clientId, limit],
    useCallback(() => {
      setObserved(new Set());
      setLoading(false);
    }, [])
  );

  const servers = useMemo(
    () => managed.filter((s) => observed.has(s.id)),
    [managed, observed]
  );

  return {
    servers,
    observedServerIds: observed,
    loading: loading || managedLoading,
  };
};
